import React, { createContext, useContext, useState, useMemo, useEffect } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { createEnhancedTheme } from '../theme/enhancedTheme';

const ColorModeContext = createContext({ mode: 'light', toggleColorMode: () => {} });
export const useColorMode = () => useContext(ColorModeContext);

const BASE_PALETTE = {
  primary: {
    main: '#1976d2',
  },
  secondary: {
    main: '#dc004e',
  },
  brand: {
    main: '#1565c0',
    light: '#e3f2fd',
    dark: '#0d47a1',
    contrastText: '#ffffff',
  },
  influencer: {
    main: '#7b1fa2',
    light: '#f3e5f5',
    dark: '#4a148c',
    contrastText: '#ffffff',
  },
  success: {
    main: '#2e7d32',
    light: '#4caf50',
    dark: '#1b5e20',
  },
  warning: {
    main: '#ed6c02',
    light: '#ff9800',
    dark: '#e65100',
  },
  error: {
    main: '#d32f2f',
    light: '#ef5350',
    dark: '#c62828',
  },
  info: {
    main: '#0288d1',
    light: '#03a9f4',
    dark: '#01579b',
  },
};

export const ColorModeProvider = ({ children }) => {
  const [mode, setMode] = useState(() => localStorage.getItem('infhire-theme') || 'light');

  const toggleColorMode = () =>
    setMode(prev => {
      const next = prev === 'light' ? 'dark' : 'light';
      localStorage.setItem('infhire-theme', next);
      return next;
    });

  useEffect(() => {
    document.body.setAttribute('data-theme', mode);
  }, [mode]);

  const theme = useMemo(() => {
    const base = createTheme({
      palette: { ...BASE_PALETTE, mode },
      typography: {
        fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
        h1: { fontWeight: 700 },
        h2: { fontWeight: 600 },
        h3: { fontWeight: 600 },
        button: { textTransform: 'none', fontWeight: 500 },
      },
      shape: { borderRadius: 8 },
      components: {
        MuiButton: { styleOverrides: { root: { borderRadius: 8 } } },
        MuiCard: { styleOverrides: { root: { borderRadius: 12, boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)' } } },
        MuiChip: { styleOverrides: { root: { fontWeight: 500 } } },
      },
    });
    return createEnhancedTheme(base, mode);
  }, [mode]);

  return (
    <ColorModeContext.Provider value={{ mode, toggleColorMode }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
};
